import {HttpClient} from '@angular/common/http';
import {Injectable, Injector} from '@angular/core';
import {Observable} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {handleResponseError, ListResponse} from '../../../common/api-helpers';
import {createPendingRequest, PendingRequest} from '../../../common/request-synchronization';
import {CommonListFilter} from '../../../models/filters/common-list-filter';
import {AppConfigService} from '../../../services/config.service';
import {SecurityAutocompleteService} from '../../security-autocomplete';
import {getSecurityKeyFromFilterCriteria} from '../security-filter-criterion-utils';
import {
    AspreaderSecurity,
    AspreaderSecurityConfig,
    isAsecurityKey,
    prepareAsecurityConfigForApi
} from './aspreader-security';

@Injectable()
export class AspreaderSecurityService {

    private readonly aspreaderSecuritiesUrl = this.appConfigService.api['autospreaderSecurities'];

    private pendingAllSecuritiesRequest: PendingRequest<AspreaderSecurity[]>;

    constructor(
        private appConfigService: AppConfigService,
        private httpClient: HttpClient,
        private injector: Injector
    ) {}

    public isAspreaderSecurityRequest(listFilter: CommonListFilter): boolean {
        const isFilterByKeyAcceptable = (): boolean => {
            const securityKey = getSecurityKeyFromFilterCriteria(listFilter.filters);

            return !!securityKey
                && isAsecurityKey(securityKey);
        };

        return !!listFilter
            && !!listFilter.filters
            && listFilter.filters.length === 3 // Count of securityKey fields
            && listFilter.pageSize === 1
            && !listFilter.pageIndex
            && isFilterByKeyAcceptable();
    }

    /**
     * @deprecated Use {@link AspreaderSecurityVersionService.emulateSecurityRestRequest} instead,
     * as it resolves versions.
     */
    public emulateSecurityRestRequest(listFilter: CommonListFilter): Observable<ListResponse<AspreaderSecurity>> {
        const securityKey = getSecurityKeyFromFilterCriteria(listFilter.filters);

        return this.fetchFilteredAspreaderSecurities().pipe(
            map(all => all.find(asecurity => asecurity.symbol === securityKey.symbol)),
            map<AspreaderSecurity | undefined, ListResponse<AspreaderSecurity>>(asecurity => ({
                data:     asecurity ? [asecurity] : [],
                listInfo: {
                    pageSize:  1,
                    pageIndex: 0,
                    pageCount: 1
                }
            }))
        );
    }

    /**
     * @deprecated Use {@link AspreaderSecurityVersionService.getFilteredLastVersionSecurities} instead,
     * as it resolves versions.
     */
    public fetchFilteredAspreaderSecurities(nameFilter?: string): Observable<AspreaderSecurity[]> {
        nameFilter = nameFilter && nameFilter.trim().toLocaleLowerCase();

        const pendingRequest = this.pendingAllSecuritiesRequest
            || createPendingRequest(
                this.requestAspreaderSecurities(),
                newPendingRequest => this.pendingAllSecuritiesRequest = newPendingRequest,
                () => this.pendingAllSecuritiesRequest = undefined
            );

        return pendingRequest.request$.pipe(
            map(all => {
                const result: AspreaderSecurity[] = nameFilter
                    ? all.filter(s => SecurityAutocompleteService.isSubStrIncludedInStrByWords(s.name, nameFilter))
                    : [...all]; // Do a copy for further sorting.

                result.sort((s1, s2) =>
                    s1.name.localeCompare(s2.name, undefined, {numeric: true})
                );

                return result;
            })
        );
    }

    public createAspreaderSecurity(asecurityConfig: AspreaderSecurityConfig): Observable<AspreaderSecurity> {
        const apiModel = prepareAsecurityConfigForApi(asecurityConfig);

        return this.httpClient.post<AspreaderSecurity>(this.aspreaderSecuritiesUrl, apiModel).pipe(
            catchError(handleResponseError)
        );
    }

    public deactivateAspreaderSecurity(asecurityId: number): Observable<unknown> {
        const url = `${this.aspreaderSecuritiesUrl}/${asecurityId}`;

        return this.httpClient.delete(url).pipe(
            catchError(handleResponseError)
        );
    }

    private requestAspreaderSecurities(): Observable<AspreaderSecurity[]> {
        return this.httpClient.get<AspreaderSecurity[]>(this.aspreaderSecuritiesUrl).pipe(
            catchError(handleResponseError)
        );
    }

}
